import { Box, useTheme, alpha } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import type { RoadmapProductSlug } from "../../api/client";

interface ProductChipProps {
  productSlug: RoadmapProductSlug;
  label: string;
  href: string;
}

// Hover transitions to coral — that's the affordance signaling "this routes
// you out of the roadmap into a product." Per spec §6.7, max 2 chips per
// task; the parent (TaskCard) enforces the cap.
export default function ProductChip({ productSlug, label, href }: ProductChipProps) {
  const theme = useTheme();
  const isExternal = /^https?:\/\//i.test(href);

  const baseSx = {
    display: "inline-flex",
    alignItems: "center",
    px: 1,
    py: 0.5,
    borderRadius: "999px",
    fontSize: 10.5,
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase" as const,
    textDecoration: "none",
    border: "1px solid",
    borderColor: "divider",
    bgcolor: alpha(theme.palette.text.secondary, 0.06),
    color: "text.secondary",
    transition: theme.transitions.create(
      ["background-color", "border-color", "color"],
      { duration: theme.transitions.duration.shortest },
    ),
    "&:hover": {
      borderColor: "secondary.main",
      bgcolor: alpha(theme.palette.secondary.main, 0.1),
      color: "secondary.main",
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.secondary.main}`,
      outlineOffset: 2,
    },
  };

  if (isExternal) {
    return (
      <Box
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-product={productSlug}
        sx={baseSx}
      >
        {label}
      </Box>
    );
  }

  return (
    <Box component={RouterLink} to={href} data-product={productSlug} sx={baseSx}>
      {label}
    </Box>
  );
}
